import type { LC_MenuItemOut, MenuItem } from './menu.types';
import { getRouteByCode } from '@/routes';
import type { ScreenPermission, SectionPermission } from '@/api/domains/authorization/permission/permissions.types';


export const mapMenuOutToModel = (dto: LC_MenuItemOut, parentIcon?: string | null): MenuItem => {
  // ⭐ Solo usamos el code del backend, todo lo demás viene de routes.tsx
  const routeDef = dto.code ? getRouteByCode(dto.code) : undefined;
  const resolvedIcon = routeDef?.icon ?? parentIcon ?? null;
  
  return {
    id: dto.id,
    code: dto.code,
    title: dto.title,
    icon: resolvedIcon,
    url: routeDef?.path ?? null,
    externalKind: routeDef?.externalKind,
    relatedWords: dto.relatedWords ?? null,
    children: (dto.children || []).map((child) => mapMenuOutToModel(child, resolvedIcon)),

    // ⭐ Metadatos enriquecidos desde routes.tsx
    routeMetadata: routeDef ? {
      iconName: routeDef.icon ?? parentIcon ?? undefined,
      breadcrumbTitle: routeDef.breadcrumb.title,
      componentName: routeDef.component.name,
    } : undefined,
  };
};

/**
 * Recursively extracts screen-level permissions from the enriched menu tree.
 * For each leaf item that has a code and permissions array, it builds a ScreenPermission
 * with per-section granularity. Screen-level flags are derived:
 *   canView = at least one section has canRead
 *   canCreate/canEdit/canDelete = OR across all sections
 */
export const extractPermissionsFromMenu = (
  items: LC_MenuItemOut[],
): Record<string, ScreenPermission> => {
  const result: Record<string, ScreenPermission> = {};

  const walk = (nodes: LC_MenuItemOut[]): void => {
    for (const node of nodes) {
      if (node.code && node.permissions && node.permissions.length > 0) {
        const sections: Record<string, SectionPermission> = {};
        let canView = false;
        let canCreate = false;
        let canEdit = false;
        let canDelete = false;

        for (const sectionPermission of node.permissions) {
          const sectionFlags: SectionPermission = {
            canView: sectionPermission.canRead,
            canCreate: sectionPermission.canCreate,
            canEdit: sectionPermission.canEdit,
            canDelete: sectionPermission.canDelete,
          };
          sections[sectionPermission.sectionName] = sectionFlags;

          if (sectionFlags.canView) canView = true;
          if (sectionFlags.canCreate) canCreate = true;
          if (sectionFlags.canEdit) canEdit = true;
          if (sectionFlags.canDelete) canDelete = true;
        }

        result[node.code] = { code: node.code, canView, canCreate, canEdit, canDelete, sections };
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(items);
  return result;
};

/**
 * Some backends may include a root item like "Configuración"; this util separates it
 * to render it on the right as per UI design. If not found, returns as-is.
 */
export const splitConfigMenuIfNeeded = (items: MenuItem[]) => {
  const index = items.findIndex(item => item.title?.toLowerCase() === 'configuración');
  if (index === -1) return { main: items, config: null as MenuItem | null };
  const copy = items.slice();
  const [config] = copy.splice(index, 1);
  return { main: copy, config };
};
